import { useEffect, useState } from "react";
import type { Swatch } from "@/mocks/colors";

type Props = {
  swatch: Swatch;
  categoryName: string;
  onClose: () => void;
};

function CopyRow({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  const doCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* noop */
    }
  };
  return (
    <div className="flex items-center justify-between gap-3 rounded-md border border-background-200 bg-background-50 px-3 py-2">
      <div className="flex items-center gap-3">
        <span className="text-xs text-foreground-500 w-11 shrink-0">{label}</span>
        <code className="font-number text-[15px] text-foreground-950 tracking-wide">{value}</code>
      </div>
      <button
        type="button"
        onClick={doCopy}
        className="cursor-pointer whitespace-nowrap text-xs px-2.5 py-1 rounded bg-background-100 text-foreground-600 hover:text-accent-700 hover:bg-accent-100 transition-colors"
      >
        {copied ? "已复制" : "复制"}
      </button>
    </div>
  );
}

export default function SwatchModal({ swatch, categoryName, onClose }: Props) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center bg-foreground-950/50 backdrop-blur-sm p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-label={`${swatch.name} 色号详情`}
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-3xl max-h-[92vh] overflow-y-auto bg-background-100 rounded-t-2xl sm:rounded-2xl border border-background-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-background-200 sticky top-0 bg-background-100/95 backdrop-blur">
          <p className="text-sm text-foreground-600">
            <span className="text-accent-700 font-medium">{categoryName}</span> · 色号明细
          </p>
          <button
            type="button"
            onClick={onClose}
            className="cursor-pointer w-9 h-9 flex items-center justify-center rounded-full bg-background-50 border border-background-200 text-foreground-700 hover:text-foreground-950"
            aria-label="关闭"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2">
          <div className="relative min-h-[200px] sm:min-h-[300px]" style={{ backgroundColor: swatch.hex }}>
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.18),transparent_55%)]" />
            <div className="absolute bottom-4 left-5 text-background-50">
              <p className="font-number text-[12px] tracking-[0.25em] opacity-80">HLC · {swatch.code}</p>
              <p className="font-serif text-2xl mt-1">{swatch.name}</p>
            </div>
          </div>
          <div className="p-5 flex flex-col gap-4">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs px-2.5 py-1 rounded-full bg-background-200 text-foreground-800">{swatch.sheen}</span>
              <span className="text-xs px-2.5 py-1 rounded-full bg-background-200 text-foreground-800">{swatch.tone}</span>
              <span className="text-xs px-2.5 py-1 rounded-full bg-accent-100 text-accent-800">明度 {swatch.lightness}%</span>
            </div>
            <p className="text-[14px] leading-7 text-foreground-700">{swatch.desc}</p>
            <div className="flex flex-col gap-2">
              <CopyRow label="HEX" value={swatch.hex} />
              <CopyRow label="RGB" value={swatch.rgb} />
              <CopyRow label="CMYK" value={swatch.cmyk} />
            </div>
            <div>
              <p className="text-xs text-foreground-500 mb-2">适用场景</p>
              <div className="flex flex-wrap gap-2">
                {swatch.use.map((u) => (
                  <span key={u} className="text-xs text-foreground-700 bg-background-50 border border-background-200 px-2.5 py-1 rounded-md">
                    {u}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}